import { DateTime } from 'luxon';
import { AvailabilityType, SpeakingSessionExaminerAllocationDto, SpeakingSessionExaminerAvailabilityDto, SpeakingSessionExaminerDto } from 'api/planning';
import { ExaminerAvailabilityFilterType } from '../ManageExaminerToRoomStructureFilterBar';

export type ExaminerWithAvailability = SpeakingSessionExaminerDto & {
  availability: ExaminerAvailabilityFilterType;
  availabilities: SpeakingSessionExaminerAvailabilityDto[];
  unavailabilities: SpeakingSessionExaminerAvailabilityDto[];
};

export const calculateExaminersAvailability = (
  examiners: SpeakingSessionExaminerDto[],
  allocations: SpeakingSessionExaminerAllocationDto[],
  availabilities: SpeakingSessionExaminerAvailabilityDto[],
  structureStartDateTime: DateTime,
  structureEndDateTime: DateTime,
  assignedExaminerId?: number
): ExaminerWithAvailability[] => {
  return examiners
    .filter((examiner) => {
      if (assignedExaminerId && examiner.id === assignedExaminerId) {
        return true;
      }

      const examinerAllocations = allocations.filter((a) => a.examinerId === examiner.id);

      const hasOverlappingAllocation = examinerAllocations.some((allocation) => {
        const allocationStart = DateTime.fromISO(allocation.startDateTime);
        const allocationEnd = DateTime.fromISO(allocation.endDateTime);

        return structureStartDateTime < allocationEnd && allocationStart < structureEndDateTime;
      });

      return !hasOverlappingAllocation;
    })
    .map((examiner) => {
      const examinerAvailability = calculateExaminerAvailability(
        examiner.id,
        availabilities,
        structureStartDateTime,
        structureEndDateTime
      );
      return {
        ...examiner,
        availability: examinerAvailability.availability,
        availabilities: examinerAvailability.availabilities,
        unavailabilities: examinerAvailability.unavailabilities,
      };
    });
};

export const calculateExaminerAvailability = (
  examinerId: number,
  availabilities: SpeakingSessionExaminerAvailabilityDto[],
  structureStartDateTime: DateTime,
  structureEndDateTime: DateTime
): (
    {
      availability: ExaminerAvailabilityFilterType,
      availabilities: SpeakingSessionExaminerAvailabilityDto[],
      unavailabilities: SpeakingSessionExaminerAvailabilityDto[]
    }
  ) => {
  const examinerAvailabilities = availabilities.filter(
    (a) => a.examinerId === examinerId && a.availabilityType === AvailabilityType.Available
  );
  const examinerUnavailabilities = availabilities.filter(
    (a) => a.examinerId === examinerId && a.availabilityType === AvailabilityType.Unavailable
  );

  const availabilityPeriods = examinerAvailabilities.map((a) => ({
    start: DateTime.fromISO(a.startDateTime),
    end: DateTime.fromISO(a.endDateTime),
  }));

  const isFullyAvailable = availabilityPeriods.some(
    (period) => period.start <= structureStartDateTime && structureEndDateTime <= period.end
  );

  if (isFullyAvailable) {
    return {
      availability: ExaminerAvailabilityFilterType.Available,
      availabilities: examinerAvailabilities,
      unavailabilities: examinerUnavailabilities
    };
  }

  const isPartiallyAvailable = availabilityPeriods.some(
    (period) => structureStartDateTime < period.end && period.start < structureEndDateTime
  );

  if (isPartiallyAvailable) {
    return {
      availability: ExaminerAvailabilityFilterType.Partially,
      availabilities: examinerAvailabilities,
      unavailabilities: examinerUnavailabilities
    };
  }

  const unavailabilityPeriods = examinerUnavailabilities.map((a) => ({
    start: DateTime.fromISO(a.startDateTime),
    end: DateTime.fromISO(a.endDateTime),
  }));

  const isUnavailable = unavailabilityPeriods.some(
    (period) => period.start < structureEndDateTime && structureStartDateTime < period.end
  );

  if (isUnavailable) {
    return {
      availability: ExaminerAvailabilityFilterType.Unavailable,
      availabilities: examinerAvailabilities,
      unavailabilities: examinerUnavailabilities
    };
  }

  return {
    availability: ExaminerAvailabilityFilterType.NotProvided,
    availabilities: examinerAvailabilities,
    unavailabilities: examinerUnavailabilities
  };
};